import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ThemeProvider } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import { HcSubNavBar } from '@paypalcorp/helpcomponents';
import { useDispatch } from 'react-redux';
import { MppFooterComponent, MppHeaderComponent } from '../../../js/home/components/MppHeaderFooter';
import { subNavBarFPTIs } from '../../helpers/analytics/appLevelAnalytics';
import AlertsPanel from '../containers/panels/AlertsPanel';
import { pageNames } from '../../common/constants';
import {
  getHeaderStyleByAccountType,
  getCustomerJourneyAPISessionKey,
  setCustomerJourneyAPISessionKey,
} from '../../helpers/globalHelper';
import LoaderOverlay from '../../shared/LoaderOverlay';
import AuthConfirmationPrompt from '../../components/authConfirmationPrompt/AuthConfirmationPrompt';
import { initAuthPrompt } from '../../components/authConfirmationPrompt/AuthConfirmationPrompt-Actions';
import {
  getCsrfToken,
  getLoaderTimeout,
  isCustomerJourneyAPIRCEnabled,
  isLoggedIn,
  isHomePage,
  isShowAuthConfirmStepUpPrompt,
} from '../../helpers/twHelper';
import { customerJourneyAPIFPTI } from '../../helpers/analytics/homePageAnalytics';
import { trackActivityEvent } from '../../helpers/analytics/fptiHelper';
import SessionHandler from '../containers/SessionHandler';
import {
  isBotExperienceEnabled,
  isShowOnlySearchLayoutEnabled,
  isHomeExpTransactionLayoutEnabled,
} from '../../../experimentations/helper';
import { fetchAllTransactions } from '../../../experimentations/actions/transaction-list';

export const PP_REACT_THEME = 'v2';
export const DEFAULT_SPOT_NAME = 'get-help';

const HomeApp = (props) => {
  const dispatch = useDispatch();
  const searchOnly = isShowOnlySearchLayoutEnabled() || isBotExperienceEnabled();
  const helpCenterContentContainer = `sh_root__main__container header_${getHeaderStyleByAccountType()} ${
    searchOnly ? 'search-only' : ''
  }`;
  const [enableSpinner, disableSpinner] = useState(true);

  // If new transaction experiment layout is enabled, fetch all transactions.
  // If transaction list is empty or if API fails, we need to fallback to the new experience home page
  useEffect(() => {
    if (isLoggedIn() && isHomeExpTransactionLayoutEnabled() && isHomePage()) {
      dispatch(fetchAllTransactions());
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn() && isHomePage()) {
      // Initalize Auth prompt display logic for on-call users
      dispatch(initAuthPrompt(isShowAuthConfirmStepUpPrompt()));
    }
  }, []);

  // Loader component
  // TODO:: this loader needs to be moved to onComponentMount using useEffect.
  const renderOverlayLoader = () => {
    const loaderTimeOut = getLoaderTimeout();
    // Disabling loader using set timeout function
    setTimeout(() => {
      disableSpinner(false);
    }, loaderTimeOut);
    return <LoaderOverlay display={enableSpinner} />;
  };

  const customerJourneyAPISessionKey = getCustomerJourneyAPISessionKey();
  const customerJourneyAPIRCEnabled = isCustomerJourneyAPIRCEnabled();
  if (isLoggedIn() && !customerJourneyAPISessionKey && customerJourneyAPIRCEnabled) {
    setCustomerJourneyAPISessionKey(true); // setting in the session as customer journey is called
    trackActivityEvent(customerJourneyAPIFPTI.customerJourneyAPICalled);
    axios
      .post('/smarthelp/customer-journey', {
        _csrf: getCsrfToken(),
      })
      .then(() => {
        trackActivityEvent(customerJourneyAPIFPTI.customerJourneyAPISuccess);
      })
      .catch(() => {
        trackActivityEvent(customerJourneyAPIFPTI.customerJourneyFailure);
      });
  }

  // Home components
  const renderHomeComponents = () => (
    <div className="sh_root__container" data-testid="sh-root-container">
      <MppHeaderComponent />
      <div className={helpCenterContentContainer} data-testid="sh-root-main-container">
        <SessionHandler />
        <HcSubNavBar activeMenu="helpCenterHome" appName="help_center" fptiOptions={subNavBarFPTIs.subNavBarHome} />
        {/* TODO:: This Page Name needs to be changed when we are loading alerts in multiple pages. */}
        <AlertsPanel pageName={pageNames.HOME} />
        <ThemeProvider theme={PP_REACT_THEME}>
          <div className="sh_root__content__container" data-testid="sh-root-content-container">
            {props.children}
          </div>
        </ThemeProvider>
      </div>
      <MppFooterComponent />
      <AuthConfirmationPrompt />
    </div>
  );

  const homePageContainer = () => (
    <div>
      {renderOverlayLoader()}
      {renderHomeComponents()}
    </div>
  );

  return homePageContainer();
};

HomeApp.propTypes = {
  children: PropTypes.element.isRequired,
};

export default HomeApp;
